/**
 * The first-party visitor identity every Privacy Bee surface shares.
 *
 * On `*.privacybee.com` one cookie ties a marketing-site landing to a sign-up
 * on auth, a purchase on checkout and a session in hive. Every surface there
 * mints and refreshes it with byte-identical attributes — a mismatch on any one
 * of them silently forks a visitor into two.
 *
 * On any other host the cookie is host-only, because a browser rejects a
 * `Domain` that does not domain-match the request host (RFC 6265 §5.3): a
 * campaign site on its own domain would otherwise re-mint a visitor on every
 * page load and persist nothing. Host-only means its visitors are its own —
 * they join nothing on privacybee.com, so attribution across that gap has to
 * ride the URL the visitor is sent on with.
 */
/** Cookie name. JS-readable on purpose: apps forward it as `x-pb-visitor-id`. */
export const VISITOR_COOKIE = 'pb_vid';
/** Shared across *.privacybee.com so a landing visit joins to a later purchase. */
export const VISITOR_COOKIE_DOMAIN = '.privacybee.com';
/** Rolling 90-day window — refreshed on every trackable pageview. */
export const VISITOR_TTL_SECONDS = 60 * 60 * 24 * 90;
/**
 * The shape the ingestion API validates. Any value that fails this is not a
 * visitor id we minted, so it is replaced rather than echoed: an id we do not
 * control would ride into a `Set-Cookie` header and into the ingest payload,
 * where it either injects cookie attributes or fails validation and discards
 * the whole pageview.
 */
const VISITOR_ID_PATTERN = /^vis_[a-zA-Z0-9]+$/;
export function isVisitorId(value) {
    return typeof value === 'string' && VISITOR_ID_PATTERN.test(value);
}
/**
 * Mint a visitor ID the ingestion API accepts as-is. It validates
 * `^vis_[a-zA-Z0-9]+$`, so the UUID's hyphens are stripped — 32 hex ≈ 122 bits.
 */
export function newVisitorId() {
    return 'vis_' + crypto.randomUUID().replace(/-/g, '');
}
/**
 * The `Domain` this host's cookie may carry, or null for a host-only cookie.
 *
 * The apex and its subdomains qualify and nothing else: the suffix test keeps
 * the leading dot, so `evil-privacybee.com` and `privacybee.com.evil.test` —
 * other people's hosts that merely read alike — get a host-only cookie.
 */
export function visitorCookieDomain(hostname) {
    const apex = VISITOR_COOKIE_DOMAIN.slice(1);
    const host = hostname.toLowerCase();
    const shared = host === apex || host.endsWith(VISITOR_COOKIE_DOMAIN);
    return shared ? VISITOR_COOKIE_DOMAIN : null;
}
/**
 * The `Set-Cookie` value that starts — or rolls forward — the 90-day window.
 * Written on every trackable pageview so the window runs from the visitor's
 * last visit, not their first.
 *
 * Takes the request's own host because that is what decides the cookie's reach:
 * shared with the rest of privacybee.com, or host-only.
 */
export function visitorSetCookie(visitorId, hostname) {
    const domain = visitorCookieDomain(hostname);
    const scope = domain === null ? '' : `; Domain=${domain}`;
    return `${VISITOR_COOKIE}=${visitorId}${scope}; Path=/; Max-Age=${VISITOR_TTL_SECONDS}; Secure; SameSite=Lax`;
}
